import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Book3D from "@/components/Book3D";
import { ShareButtons } from "@/components/Forms";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/lib/SiteContext";
import { track } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const FAQ = [
  {
    q: "Is Dirty Church Chronicles fiction?",
    a: "No. Dirty Church Chronicles is Christian nonfiction, written with an apostolic and prophetic voice. Its themes include repentance, truth, accountability, discernment, and restoration.",
  },
  {
    q: "How many chronicles are in Volume I?",
    a: "Volume I contains 111 chronicles.",
  },
  {
    q: "Where can I buy the book?",
    a: "The Kindle edition is available now on Amazon. Use the purchase button on this page — Amazon shows the current price at checkout.",
  },
  {
    q: "Will there be a paperback or hardcover edition?",
    a: "Print editions are in preparation. Join the reader list on the Connect page to be notified when they are available.",
  },
  {
    q: "Can I order bulk copies or invite the author to speak?",
    a: "Yes. Use the inquiry form on the Connect page and choose the category that fits — speaking, media, or bulk copies.",
  },
];

export default function Book() {
  const { settings, volume } = useSite();
  const available = (volume.formats || []).filter((f) => f.status === "available" && f.url);
  const coming = volume.coming || [];

  return (
    <div className="bg-ink" data-testid="book-page">
      <Seo
        title={`${volume.title || "Dirty Church Chronicles: Volume I"} — ${settings.author_name || "Jermaine Miller"}`}
        description={settings.seo_description}
        path="/book"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: volume.title || "Dirty Church Chronicles: Volume I",
          author: { "@type": "Person", name: settings.author_name || "Jermaine Miller" },
          bookFormat: "https://schema.org/EBook",
          inLanguage: "en",
          url: "https://www.amazon.com/dp/B0HLV2X8VB",
        }}
      />
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 book-spotlight" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center w-full">
          <Reveal className="lg:col-span-5 flex justify-center">
            <Book3D cover={settings.cover_url} spine={settings.spine_url} back={settings.back_url} title={volume.title} author={settings.author_name} />
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-5">Christian Nonfiction · {volume.chronicle_count || 111} Chronicles</p>
              <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight" data-testid="book-title">
                {volume.title || "Dirty Church Chronicles: Volume I"}
              </h1>
              <p className="font-reading italic text-xl text-gold/90 mt-4">by {settings.author_name || "Jermaine Miller"}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="font-reading text-xl leading-relaxed text-paper/80 mt-8 max-w-2xl" data-testid="book-synopsis">
                {volume.synopsis}
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-10">
              <p className="eyebrow mb-4">Available Formats</p>
              <div className="flex flex-wrap gap-4" data-testid="format-selector">
                {available.map((f) => (
                  <a
                    key={f.key}
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`format-${f.key}-btn`}
                    onClick={() => track("purchase_click", f.key)}
                    className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.22em] font-semibold bg-crimson text-paper px-7 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]"
                  >
                    {f.label} {f.price ? `· ${f.price}` : ""} <ArrowUpRight size={14} />
                  </a>
                ))}
              </div>
              <p className="font-ui text-xs text-paper/40 mt-4">
                Purchase is completed on Amazon, where current pricing is shown.
                {coming.length > 0 && ` ${coming.join(" and ")} ${coming.length > 1 ? "editions are" : "edition is"} in preparation.`}
              </p>
              <p className="font-ui text-xs mt-4">
                <a href="/bookstore" data-testid="book-bookstore-link" className="text-crimsonhi uppercase tracking-[0.2em] hover:text-paper transition-colors duration-300">
                  Visit the Official Bookstore →
                </a>
              </p>
              <div className="mt-8 text-paper/60">
                <ShareButtons title={volume.title} text={settings.hero_sub} path="/book" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="paper-section py-24 sm:py-32" data-testid="book-faq-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="eyebrow eyebrow-light mb-4">Questions</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-ink mb-10">About the Book</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="book-faq">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-ink/15">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-display text-lg text-ink text-left hover:text-crimson">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-reading text-lg leading-relaxed text-ink/75">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
